import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { getDefaultPrimaryBeneficiary } from './defaultPrimaryBeneficiary';

const StyledDiv = styled.div`
  .help-text {
    margin-bottom: ${SPACERS.tiny};
  }
`;

export const getHelpText = (
  questionId: number,
  answers: Answer[] | undefined,
  defaultText: string | null | undefined,
  beneficiary: string | undefined,
  intermediaries: string | undefined,
) => {
  if (!answers) return defaultText;
  const beneficiaryTerm =
    !beneficiary || beneficiary === 'Animals' ? 'People' : beneficiary;
  switch (questionId) {
    case 40002:
      return 'This will help us tailor your report to its specific area of focus.';
    case 40003: {
      const cause = answers.find(x => x.questionId === 40002)?.text;
      return (
        <>
          <p>
            These are the templates associated with your cause selection:{' '}
            <strong>{cause || 'Unknown'}.</strong>
          </p>
          <p>
            It’s OK if it’s not an exact 1-for-1 match. Feel free to select the
            one that most closely aligns with the service your program provides.
          </p>
        </>
      );
    }
    case 40004: {
      const service =
        answers.find(x => x.questionId === 40003)?.text || 'Loading...';
      const defaultBeneficiaryName = getDefaultPrimaryBeneficiary(service);
      return (
        <TrueImpactText variant="body">
          <p>
            Many people benefit from {service} programs like yours. But here we
            are interested in{' '}
            <strong>
              defining the term you use for your direct end beneficiary
            </strong>{' '}
            (i.e., the people you are helping gain new or better employment).
          </p>
          {defaultBeneficiaryName && (
            <p>
              Many programs call them{' '}
              <strong>
                {defaultBeneficiaryName.toLowerCase() || 'beneficiaries'}
              </strong>
              . If this is correct, select it below and continue.
            </p>
          )}
        </TrueImpactText>
      );
    }
    case 40006: {
      const serviceForAchievement = answers.find(x => x.questionId === 40003)
        ?.text;
      const primarilyHelp = answers.find(x => x.questionId === 40004)?.text;
      return (
        <p>
          This helps to pinpoint the way in which{' '}
          <strong>{primarilyHelp?.toLowerCase() || 'People'}</strong> benefit
          from your work in{' '}
          <strong>{serviceForAchievement || 'Unknown'}</strong>.
        </p>
      );
    }

    case 40007:
      return (
        <StyledDiv>
          <TrueImpactText variant="heading-subheading" color="ctaBrandOrange">
            This is to include the <strong>Learn Stage</strong> in your impact
            model
            <br />
            (if relevant).
          </TrueImpactText>
          <div className="help-text">
            Only select “Yes” if you track beneficiary learning as an interim
            outcome unique from your end outcome(s).
          </div>
        </StyledDiv>
      );

    case 40008:
      return (
        <StyledDiv>
          <TrueImpactText variant="heading-subheading" color="ctaBrandOrange">
            This is to include the <strong>Act Stage</strong> in your impact
            model
            <br />
            (if relevant).
          </TrueImpactText>
          <div className="help-text">
            Only select “Yes” if you track beneficiary behavior change as an
            interim outcome unique from your end outcome(s).
          </div>
        </StyledDiv>
      );

    default:
      return defaultText;
  }
};
